import React, { FC } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { rem } from 'polished';
import flattenChildren from 'react-keyed-flatten-children';
import {
  animations,
  colors,
  text,
  spaces,
  mixins,
} from '../../../core/styles/theme/default';
import ImageWithCaption from '../../../components/Image/ImageWithCaption';
import { getTrackingValues, mergeClassNames } from '../../../core/utils/Utils';
import { A11yText } from '../../../helpers';
import { LinkProps } from '../Link';
import IconSVG from '../../../primitives/IconSVG/IconSVG';

const StyledLink = styled.a(
  ({ color, iconOnly, iconHover, backgroundImage, hamburger, theme }: any) => {
    const isPrimary = color === 'primary';
    const isSecondary = color === 'secondary';
    const isLight = color === 'light' || backgroundImage === true;
    const isCustom = !!color && !isPrimary && !isSecondary && !isLight;

    const defaultStyles = css`
      display: inline-flex;
      align-items: center;
      text-decoration: none;
      ${text.p3};
      font-weight: 500;
      transition: all ${animations.default};
      transition-property: border-color, background;
      background-color: transparent;
      border: none;
      cursor: pointer;

      ${backgroundImage &&
      css`
        position: relative;

        .label,
        svg {
          z-index: 2;
        }
      `}

      ${hamburger &&
      css`
        .label {
          font-size: ${rem(14)};
        }
      `}

      ${iconHover &&
      css`
        @keyframes iconHoverLink {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }

        .label ~ svg {
          position: absolute;
          left: 0;
          top: 0;
          opacity: 0;
        }
        .label + svg {
          position: static;
          opacity: 1;
        }

        &:hover {
          .label ~ svg {
            position: static;
            opacity: 1;
          }
          .label + svg {
            position: absolute;
            right: 0;
            top: 0;
            opacity: 0;
          }
        }
      `}

      ${!iconOnly &&
      !hamburger &&
      css`
        &:hover,
        &:focus {
          .label {
            position: relative;

            &::after {
              content: '';
              border-bottom: ${rem(1)} solid currentColor;
              width: 100%;
              position: absolute;
              left: 0;
              bottom: 2px;
            }
          }
        }
      `}
    `;

    const defaultColor = css`
      color: ${colors.primary};

      ${!iconOnly &&
      css`
        &:hover {
          color: ${colors.primary};
        }
      `}
    `;

    const lightColor = css`
      color: ${colors.white};
      font-weight: 400;

      ${!iconOnly &&
      css`
        &:hover,
        &:focus {
          color: ${colors.nightBlue300};

          .label {
            &::after {
              display: none;
            }
          }
        }
      `}
    `;

    const primaryColor = css`
      color: ${colors.primary};

      ${!iconOnly &&
      css`
        &:hover {
          color: ${colors.primary};
        }
      `}
    `;

    const secondaryColor = css`
      color: ${colors.secondary};

      ${!iconOnly &&
      css`
        &:hover {
          color: ${colors.secondary300};
        }
      `}
    `;

    const customColor = css`
      color: ${color};

      ${!iconOnly &&
      css`
        &:hover {
          color: rgba(${color}, 0.8);
        }
      `}

      ${theme?.stylesExtension?.linkContent?.link?.customColor(color)}
    `;

    const styles = [defaultStyles];

    if (isPrimary) {
      styles.push(primaryColor);
    } else if (isSecondary) {
      styles.push(secondaryColor);
    } else if (isLight) {
      styles.push(lightColor);
    } else if (isCustom) {
      styles.push(customColor);
    } else {
      styles.push(defaultColor);
    }

    return styles;
  },
);

const StyledPlainLink = styled.a`
  color: ${colors.grey900};
  text-decoration: none;
`;

const StyledIcon = styled(IconSVG)(({ iconOnly, before }: any) => {
  const rule = before ? 'right' : 'left';
  const cssProps: any = [];

  if (!iconOnly) {
    cssProps.push(
      `margin-${rule}: ${rem(
        spaces.beta,
      )}; color: #FF3F00; font-weight: 600; flex-shrink: 0;`,
    );
  }

  return cssProps;
});

export const StyledBackground = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  overflow: hidden;
  opacity: 1;
  background: ${colors.blue900};

  picture,
  figure,
  .gatsby-image-wrapper {
    width: 100%;
    height: 100%;
  }

  picture {
    position: absolute;
    top: 0;
    left: 0;
    opacity: 1;
    transform: scale(1.07);
    transition: opacity ${animations.default}, transform ${animations.default};
  }

  a:hover & {
    picture {
      opacity: 0.8;
      transform: scale(1);
    }
  }
`;

export const StyledShadow = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  z-index: 2;
  background: linear-gradient(
    0deg,
    rgba(0, 0, 0, 0.8) 0%,
    rgba(255, 255, 255, 0) 90%
  );
`;

const StyledHamburgerComponent = styled('span', {
  shouldForwardProp: (prop) => !['color'].includes(prop),
})(({ color }) => {
  const isLight = color === 'light';

  const defaultStyles = css`
    width: ${rem(22)};
    height: ${rem(22)};
    position: relative;
    margin-left: ${rem(16)};

    &:focus {
      outline: 0;
      border: 0;
    }

    .line {
      transition: 320ms ease-in-out, color 180ms linear;
      display: block;
      background: ${colors.blue900};
    }

    ${isLight &&
    css`
      .label {
        background: ${colors.white};
      }
    `}
  `;

  const styles = [defaultStyles];

  return styles;
});

const StyledHamburger = styled.span`
  position: absolute;
  height: 100%;
  width: 100%;
  left: 0;

  .line {
    width: 22px;
    height: 2px;
    position: relative;
    top: -2px;
    left: 0;
    margin: 5px 0;

    &:nth-of-type(1) {
      transition-delay: 0.5s;
    }
    &:nth-of-type(2) {
      transition-delay: 0.625s;
    }
    &:nth-of-type(3) {
      transition-delay: 0.75s;
      width: 11px;
    }

    .hamburger-active & {
      width: 0%;

      &:nth-of-type(1) {
        transition-delay: 0s;
      }
      &:nth-of-type(2) {
        transition-delay: 0.125s;
      }
      &:nth-of-type(3) {
        transition-delay: 0.25s;
      }
    }
  }
`;

const StyledCross = styled.span`
  position: absolute;
  height: 100%;
  width: 100%;
  transform: rotate(45deg);
  left: 0;

  .line {
    background-color: ${colors.blue900};

    &:nth-of-type(1) {
      height: 0%;
      transition-delay: 0s;

      width: 2px;
      position: absolute;
      top: 0;
      left: 8px;
    }
    &:nth-of-type(2) {
      width: 0%;
      height: 2px;
      position: absolute;
      left: -3px;
      top: 11px;
      transition-delay: 0.25s;
    }

    .hamburger-active & {
      background-color: white;

      &:nth-of-type(1) {
        height: 24px;
        transition-delay: 0.625s;
      }
      &:nth-of-type(2) {
        width: 24px;
        transition-delay: 0.375s;
      }
    }
  }
`;

const StyledTeaserIcon = styled.div(
  ({ isLarge }: { isLarge?: boolean }) => css`
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;

    ${mixins.responsiveStyles({
      width: { zero: 48, wide: 60 },
      height: { zero: 48, wide: 60 },
      top: { zero: 16, wide: 24 },
      left: { zero: 16, wide: 24 },
    })}

    ${isLarge &&
    css`
      ${mixins.responsiveStyles({
        width: { zero: 64, medium: 110, wide: 130 },
        height: { zero: 64, medium: 110, wide: 130 },
        top: { zero: 16, medium: 32 },
        left: { zero: 16, medium: '50%' },
        transform: { medium: 'translateX(-50%)' },
      })}
    `}

    figure {
      width: 100%;
    }
  `,
);

const LinkContent: FC<LinkProps> = ({
  linkText,
  link,
  iconTop,
  iconBefore,
  iconAfter,
  iconSize,
  iconHover,
  hamburger,
  iconOnly,
  tracking,
  noStyle,
  className,
  children,
  backgroundImage,
  teaserIcon,
  ...rest
}) => {
  const trackingProps = getTrackingValues({ tracking });
  const classes = mergeClassNames({ className, tracking });
  const hasIcon = !!iconAfter || !!iconBefore || !!iconHover || !!iconTop;
  const hasChildren = !!children;
  const PlainLinkType = noStyle ? 'a' : StyledPlainLink;
  const LinkType = noStyle ? 'a' : StyledLink;
  const iconProps = !noStyle && {
    iconOnly,
    backgroundImage: backgroundImage ? true : false,
    iconHover: iconHover ? true : false,
  };
  const allowedComponents = ['CollapseIcon'];

  return hasChildren ? (
    <PlainLinkType {...link} {...trackingProps} {...classes} {...rest}>
      {flattenChildren(children).map((child: any) => {
        const { type } = child;

        // only forward rest props to Elements that are listed in allowedComponents
        // ignore for code coverage - [MLi]: idk any way to test this
        /* istanbul ignore if */
        if (type && allowedComponents.some((v) => type.displayName === v)) {
          return React.cloneElement(child, { ...rest });
        }
        return child;
      })}
    </PlainLinkType>
  ) : (
    <LinkType
      {...link}
      {...trackingProps}
      {...classes}
      {...iconProps}
      {...rest}
      {...(hamburger && { as: 'button', hamburger })}
    >
      {backgroundImage ? (
        <>
          <StyledShadow />
          <StyledBackground>
            <ImageWithCaption
              data={{ ...backgroundImage, disableLink: true }}
            />
          </StyledBackground>
        </>
      ) : null}
      {iconTop && <StyledIcon iconName={iconTop} iconOnly={iconOnly} />}
      {iconBefore && (
        <>
          <StyledIcon
            iconName={iconBefore}
            iconOnly={iconOnly}
            iconSize={iconSize ? iconSize : 18}
            before
          />
        </>
      )}
      {linkText &&
        (iconOnly && hasIcon ? (
          <A11yText>{linkText}</A11yText>
        ) : React.isValidElement(linkText) ? (
          linkText
        ) : (
          <span
            className="label"
            dangerouslySetInnerHTML={{
              __html: linkText.replace(/(<([^>]+)>)/gi, ''),
            }}
          ></span>
        ))}
      {iconAfter && (
        <>
          <StyledIcon
            iconName={iconAfter}
            iconOnly={iconOnly}
            iconSize={iconSize ? iconSize : 18}
          />
        </>
      )}
      {iconHover && (
        <StyledIcon
          iconName={iconHover}
          iconOnly={iconOnly}
          iconSize={iconSize ? iconSize : 18}
        />
      )}
      {hamburger && (
        <StyledHamburgerComponent color={rest.color}>
          <StyledHamburger>
            <span className="line" />
            <span className="line" />
            <span className="line" />
          </StyledHamburger>
          <StyledCross>
            <span className="line" />
            <span className="line" />
          </StyledCross>
        </StyledHamburgerComponent>
      )}
      {teaserIcon && (
        <StyledTeaserIcon isLarge={rest.isLarge}>
          <ImageWithCaption data={{ ...teaserIcon, disableLink: true }} />
        </StyledTeaserIcon>
      )}
    </LinkType>
  );
};

export default LinkContent;
